import React, { useState, useEffect } from 'react';
import {
  CloseIcon,
  FileIcon,
  ProductCatalogIcon,
  AccountIcon,
  ArrowLeft,
  TimeIcon,
  EmailIcon,
  PhoneIcon
} from '../../../public/icons';
import adminTransactionService from '../../../services/admin/admin-transactionService';

/* ───────── helpers ───────── */
const formatNaira = (kobo) =>
  `₦${((Number(kobo) || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0
  })}`;

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

const statusLabel = (s) =>
  ({
    success: 'Completed',
    pending: 'Pending',
    failed: 'Failed',
    abandoned: 'Abandoned',
    reversed: 'Reversed'
  }[s] || 'Pending');

const PaymentDetailsModal = ({
  isOpen,
  onClose,
  transactionId,
  onViewOrder
}) => {
  const [loading, setLoading] = useState(true);
  const [txn, setTxn] = useState(null);
  const [error, setError] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  /* ───────── Fetch detail when opened ───────── */
  useEffect(() => {
    if (!isOpen || !transactionId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await adminTransactionService.getTransactionById(
          transactionId
        );
        if (cancelled) return;
        if (res.success) {
          setTxn(res.data);
          setInternalNote(res.data.internalNote || '');
        } else {
          setError(res.message || 'Failed to load transaction');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, transactionId]);

  /* ───────── Save internal note ───────── */
  const handleSaveNote = async () => {
    setSavingNote(true);
    setNoteSaved(false);
    setTimeout(() => {
      setSavingNote(false);
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    }, 500);
  };

  if (!isOpen) return null;

  /* ───────── Status badge ───────── */
  const getStatusBadge = (status) => {
    const map = {
      success: { color: 'bg-[#E7F4ED] text-[#2F8F5B]', label: 'Completed' },
      pending: { color: 'bg-[#FFF8E1] text-[#D8A100]', label: 'Pending' },
      failed: { color: 'bg-[#FFEBEE] text-[#D32F2F]', label: 'Failed' },
      abandoned: { color: 'bg-gray-100 text-gray-700', label: 'Abandoned' },
      reversed: { color: 'bg-[#F4E7E7] text-[#A82121]', label: 'Reversed' }
    };
    const cfg = map[status] || map.pending;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}
      >
        <span className="mr-1">●</span>
        {cfg.label}
      </span>
    );
  };

  /* ───────── Handle View Order click ───────── */
  const handleViewOrder = () => {
    const orderId = txn?.order?._id || txn?.order;
    if (!orderId) return;
    if (typeof onViewOrder === 'function') {
      onViewOrder(orderId);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[70%] lg:w-[60%] xl:w-[50%] bg-white z-[999] overflow-y-auto">
        <div className="h-full flex flex-col text-left">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <div className="flex gap-2 items-center">
                <h1 className="text-2xl font-bold text-[#0E1628]">
                  {loading
                    ? 'Loading…'
                    : txn?.reference || `#${transactionId}`}
                </h1>
                {!loading && txn && getStatusBadge(txn.status)}
              </div>
              {!loading && txn && (
                <p className="text-sm font-light text-[#6B635D] mt-1">
                  Placed on {formatDate(txn.createdAt)}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 border border-bordercolor"
            >
              <CloseIcon className="w-6 h-6 text-[#8A9099]" />
            </button>
          </div>

          {/* Body */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146]" />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : (
            <div className="flex-1 p-6">
              <div className="flex flex-col lg:flex-row justify-between gap-3">
                {/* ───────── LEFT COLUMN ───────── */}
                <div className="w-full lg:w-3/5 space-y-3">
                  {/* Payment Summary */}
                  <div className="bg-[#EAFFF3] border-2 border-[#A6FACC] px-5 py-4">
                    <h3 className="text-lg font-medium text-[#0E1628] mb-2">
                      Payment Summary
                    </h3>
                    <p className="text-sm text-[#8A9099] mb-4 break-all">
                      Reference: {txn.reference}
                    </p>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-[#8A9099]">Amount</span>
                        <span className="text-[#0E1628] font-medium">
                          {formatNaira(txn.amount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#8A9099]">Currency</span>
                        <span className="text-[#0E1628]">
                          {txn.currency || 'NGN'}
                        </span>
                      </div>
                      {txn.channel && (
                        <div className="flex justify-between">
                          <span className="text-[#8A9099]">Channel</span>
                          <span className="text-[#0E1628] capitalize">
                            {txn.channel}
                          </span>
                        </div>
                      )}
                      {txn.gatewayResponse && (
                        <div className="flex justify-between">
                          <span className="text-[#8A9099]">
                            Gateway Response
                          </span>
                          <span className="text-[#0E1628]">
                            {txn.gatewayResponse}
                          </span>
                        </div>
                      )}
                      <div className="border-t pt-3 mt-3">
                        <div className="flex justify-between font-medium">
                          <span className="text-[#0E1628]">Total Paid</span>
                          <span className="text-[#0E1628]">
                            {formatNaira(txn.amount)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Paystack extras */}
                    {txn.paystackResponse && (
                      <div className="mt-4 pt-4 border-t border-[#A6FACC] grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        {txn.paystackResponse.id && (
                          <div className="flex justify-between">
                            <span className="text-[#8A9099]">
                              Paystack ID
                            </span>
                            <span className="text-[#0E1628]">
                              {txn.paystackResponse.id}
                            </span>
                          </div>
                        )}
                        {txn.paystackResponse.ip_address && (
                          <div className="flex justify-between">
                            <span className="text-[#8A9099]">IP Address</span>
                            <span className="text-[#0E1628]">
                              {txn.paystackResponse.ip_address}
                            </span>
                          </div>
                        )}
                        {txn.paystackResponse.fees != null && (
                          <div className="flex justify-between">
                            <span className="text-[#8A9099]">Fees</span>
                            <span className="text-[#0E1628]">
                              {formatNaira(txn.paystackResponse.fees)}
                            </span>
                          </div>
                        )}
                        {txn.paystackResponse.authorization
                          ?.authorization_code && (
                          <div className="flex justify-between">
                            <span className="text-[#8A9099]">
                              Auth Code
                            </span>
                            <span className="text-[#0E1628] break-all">
                              {txn.paystackResponse.authorization
                                .authorization_code}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Linked Order */}
                  {txn.order && (
                    <div className="border border-gray-200">
                      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ProductCatalogIcon size={20} color="#55000C" />
                          <h3 className="text-lg font-medium text-[#0E1628]">
                            Linked Order
                          </h3>
                        </div>
                        <span className="text-xs text-[#8A9099]">
                          {txn.order.orderNumber}
                        </span>
                      </div>

                      <div className="p-4">
                        <div className="flex justify-between mb-4">
                          <span className="font-normal text-base text-[#0E1628]">
                            {txn.orderNumber || txn.order.orderNumber}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#E7F4ED] text-[#2F8F5B] capitalize">
                            {txn.order.orderStatus}
                          </span>
                        </div>

                        <button
                          onClick={handleViewOrder}
                          className="text-sm text-secondary hover:text-[#C49660] underline"
                        >
                          View Order
                        </button>

                        <div className="space-y-3 mt-4">
                          {txn.order.items?.map((item, i) => (
                            <div
                              key={i}
                              className="flex justify-between items-center"
                            >
                              <div>
                                <p className="text-base font-normal text-[#8A9099]">
                                  {item.title}
                                </p>
                                <p className="text-sm text-[#8A9099]">
                                  Qty {item.quantity}
                                </p>
                              </div>
                              <span className="text-sm font-medium text-[#0E1628]">
                                ₦
                                {(
                                  item.lineTotal ||
                                  item.unitPrice * item.quantity
                                ).toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="border border-gray-200">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <TimeIcon size={20} color="#55000C" />
                        <h3 className="text-lg font-medium text-[#0E1628]">
                          Transaction Timeline
                        </h3>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="space-y-4">
                        <TimelineRow
                          label="Payment Initiated"
                          date={formatDate(txn.initializedAt || txn.createdAt)}
                          completed
                        />
                        {txn.status === 'success' && (
                          <TimelineRow
                            label="Payment Successful"
                            date={formatDate(txn.paidAt)}
                            completed
                          />
                        )}
                        {txn.status === 'failed' && (
                          <TimelineRow
                            label="Payment Failed"
                            date={formatDate(txn.updatedAt)}
                            completed
                            failed
                          />
                        )}
                        {txn.status === 'abandoned' && (
                          <TimelineRow
                            label="Payment Abandoned"
                            date={formatDate(txn.updatedAt)}
                            completed={false}
                          />
                        )}
                        {txn.status === 'pending' && (
                          <TimelineRow
                            label="Awaiting Payment Confirmation"
                            date="Pending"
                            completed={false}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ───────── RIGHT COLUMN ───────── */}
                <div className="w-full lg:w-2/5 space-y-3">
                  {/* Customer */}
                  <div className="border bg-[#F9FAFB]">
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <AccountIcon size={20} color="#8A9099" />
                        <h3 className="text-lg font-medium text-[#0E1628]">
                          Customer
                        </h3>
                      </div>
                    </div>

                    <div className="p-4 space-y-4">
                      <p className="font-medium text-[#0E1628] text-lg">
                        {txn.customerName ||
                          `${txn.user?.firstName || ''} ${
                            txn.user?.lastName || ''
                          }`.trim() ||
                          '—'}
                      </p>
                      <div className="flex items-center gap-2">
                        <EmailIcon color="#1E1E1E" />
                        <p className="text-[#8A9099] break-all">
                          {txn.customerEmail || txn.user?.email || '—'}
                        </p>
                      </div>
                      {txn.user?.phone && (
                        <div className="flex items-center gap-2">
                          <PhoneIcon color="#1E1E1E" size="20" />
                          <p className="text-[#8A9099]">{txn.user.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Internal Note */}
                  <div className="bg-[#F9FAFB]">
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <FileIcon className="w-5 h-5" />
                        <h3 className="text-lg font-medium text-[#0E1628]">
                          Internal Note
                        </h3>
                      </div>
                    </div>

                    <div className="p-4">
                      <textarea
                        value={internalNote}
                        onChange={(e) => setInternalNote(e.target.value)}
                        className="w-full h-24 p-3 border border-gray-200 bg-transparent resize-none text-sm text-[#0E1628] focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                        placeholder="Add internal notes..."
                      />
                      <div className="flex justify-end mt-3">
                        <button
                          onClick={handleSaveNote}
                          disabled={savingNote}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            savingNote
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : noteSaved
                              ? 'bg-[#2F8F5B] text-white'
                              : 'bg-primary hover:bg-[#C49660] text-white'
                          }`}
                        >
                          {savingNote
                            ? 'Saving…'
                            : noteSaved
                            ? '✅ Saved'
                            : 'Save Note'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* ───────── Timeline row ───────── */
const TimelineRow = ({ label, date, completed, failed }) => (
  <div className="flex items-center gap-3">
    <div className="flex flex-col items-center">
      <div
        className={`w-3 h-3 rounded-full ${
          failed
            ? 'bg-[#D32F2F]'
            : completed
            ? 'bg-[#2F8F5B]'
            : 'bg-gray-300'
        }`}
      />
      <div
        className={`w-0.5 h-8 ${
          completed ? 'bg-[#2F8F5B]' : 'bg-gray-300'
        }`}
      />
    </div>
    <div>
      <p
        className={`font-medium ${
          completed || failed ? 'text-[#0E1628]' : 'text-gray-500'
        }`}
      >
        {label}
      </p>
      <p className="text-sm text-[#8A9099]">{date}</p>
    </div>
  </div>
);

export default PaymentDetailsModal;